import { useTranslations } from 'next-intl';

import type { ConfirmationModalProps } from '@/types/modal';

import { Button } from '../ui/button';
import BaseModal from './BaseModal';

function ConfirmationModal(props: ConfirmationModalProps): React.JSX.Element {
  const translate = useTranslations();
  const {
    title,
    description,
    onOpenChange,
    onConfirm,
    onReject,
    confirmText,
    rejectText,
    isLoadingContent,
    open,
    children,
    className,
    isCritical = false,
    isConfirmDisabled = false,
    isLoadingConfirmation = false,
    confirmIcon,
    footer,
  } = props;

  const finalConfirmText = confirmText ?? translate('modal.confirm');
  const finalRejectText = rejectText ?? translate('modal.cancel');

  const defaultFooter = (
    <div className="ml-auto mt-5 flex w-full items-center justify-end gap-2">
      <Button
        onClick={(e) => {
          // Prevent event propagation to avoid triggering additional handlers
          e.stopPropagation();
          // First call the onReject callback
          if (onReject) {
            onReject();
          }
          // Then manually close the modal without triggering onOpenChange
          if (onOpenChange) {
            onOpenChange(false);
          }
        }}
        variant="ghost"
      >
        {finalRejectText}
      </Button>
      <Button
        disabled={isConfirmDisabled}
        loading={isLoadingConfirmation}
        variant={isCritical ? 'error' : 'primary'}
        onClick={(e) => {
          // Prevent event propagation
          e.stopPropagation();
          // Call the onConfirm callback
          if (onConfirm) {
            onConfirm();
          }
        }}
        startIcon={confirmIcon}
      >
        {finalConfirmText}
      </Button>
    </div>
  );

  return (
    <BaseModal
      onOpenChange={onOpenChange}
      open={open}
      title={title}
      description={description}
      className={className}
      isLoadingContent={isLoadingContent}
      footer={footer || defaultFooter}
    >
      {children}
    </BaseModal>
  );
}

export default ConfirmationModal;
